import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

/** How often to poll the server for a new deployed version. */
const UPDATE_POLL_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

@Injectable({ providedIn: 'root' })
export class UpdateAppService {
   private _swUpdate = inject(SwUpdate);
   private _destroyRef = inject(DestroyRef);

   /** True when a new version has been downloaded and is ready to activate */
   updateReady = signal(false);

   /** True while the new version is being downloaded */
   downloading = signal(false);

   constructor() {
      // Force reload on unrecoverable state (corrupted cache, etc.)
      this._swUpdate.unrecoverable.subscribe(() => {
         window.location.reload();
      });
   }

   checkUpdate() {
      if (!this._swUpdate.isEnabled) return;

      this._swUpdate.versionUpdates.subscribe((evt) => {
         switch (evt.type) {
            case 'VERSION_DETECTED':
               this.downloading.set(true);
               break;
            case 'VERSION_READY':
               this.downloading.set(false);
               this.updateReady.set(true);
               break;
            case 'VERSION_INSTALLATION_FAILED':
               this.downloading.set(false);
               break;
         }
      });

      // A POS session can stay open for hours/days without a reload, so the
      // service worker never re-checks the server on its own. Poll periodically
      // and whenever the tab regains focus so a new deploy is picked up.
      this._checkForUpdate();

      const intervalId = setInterval(
         () => this._checkForUpdate(),
         UPDATE_POLL_INTERVAL_MS,
      );

      const onVisible = () => {
         if (document.visibilityState === 'visible') this._checkForUpdate();
      };
      document.addEventListener('visibilitychange', onVisible);

      this._destroyRef.onDestroy(() => {
         clearInterval(intervalId);
         document.removeEventListener('visibilitychange', onVisible);
      });
   }

   /** Reload the page to activate the already-downloaded update. */
   applyUpdate() {
      window.location.reload();
   }

   private _checkForUpdate() {
      // Never rejects fatally, but swallow errors (e.g. offline) so the poll continues.
      this._swUpdate.checkForUpdate().catch(() => {});
   }
}
